import type { Language } from '../i18n/translations'
import { newPatientExamPrice } from './homePriceRefs'
import { whatsAppLink } from '../utils/whatsapp'

export type HomeFaqLink = {
  label: string
  href: string
  external: boolean
}

export type HomeFaqItem = {
  q: string
  a: string
  link?: HomeFaqLink
}

/**
 * Homepage "Before your first visit" FAQ. The same items feed the visible accordion
 * and the FAQPage structured data, so they always match.
 */
export function buildHomeFaq(lang: Language): HomeFaqItem[] {
  const pick = (en: string, ua: string, ru: string, ro: string) =>
    lang === 'ua' ? ua : lang === 'ru' ? ru : lang === 'ro' ? ro : en
  const pricesLink: HomeFaqLink = {
    label: pick('View prices', 'Переглянути ціни', 'Посмотреть цены', 'Vezi prețurile'),
    href: `/${lang}/prices`,
    external: false,
  }
  const price = newPatientExamPrice

  return [
    {
      q: pick(
        'What does the first exam include?',
        'Що входить у перший огляд?',
        'Что входит в первый осмотр?',
        'Ce include prima examinare?',
      ),
      a: pick(
        `A new patient exam costs ${price}. It includes an exam and two X-rays; your dentist decides whether they are needed. After the exam, you can discuss the recommended next steps.`,
        `Огляд нового пацієнта коштує ${price}. У вартість входять огляд і два рентгенівські знімки; їхню необхідність визначає лікар. Після огляду ви зможете обговорити рекомендовані наступні кроки.`,
        `Осмотр нового пациента стоит ${price}. В стоимость входят осмотр и два рентгеновских снимка; их необходимость определяет врач. После осмотра вы сможете обсудить рекомендуемые дальнейшие шаги.`,
        `Examinarea unui pacient nou costă ${price}. Include examinarea și două radiografii; medicul stabilește dacă sunt necesare. După examinare, puteți discuta pașii următori recomandați.`,
      ),
      link: pricesLink,
    },
    {
      q: pick(
        'What if I feel nervous about treatment?',
        'Що робити, якщо я хвилююся перед лікуванням?',
        'Что делать, если я волнуюсь перед лечением?',
        'Ce fac dacă am emoții înainte de tratament?',
      ),
      a: pick(
        'Let us know when you book. At your appointment we will explain each stage and discuss what could help you feel more comfortable. You can ask for a pause.',
        'Повідомте нам про це під час запису. На прийомі ми пояснимо кожен етап і обговоримо, що допоможе вам почуватися комфортніше. Ви можете попросити зробити паузу.',
        'Сообщите нам об этом при записи. На приёме мы объясним каждый этап и обсудим, что поможет вам чувствовать себя комфортнее. Вы можете попросить сделать паузу.',
        'Spuneți-ne acest lucru la programare. La consultație vă explicăm fiecare etapă și discutăm ce vă poate ajuta să vă simțiți mai confortabil. Puteți cere o pauză.',
      ),
      link: {
        label: pick('Message us', 'Написати нам', 'Написать нам', 'Scrieți-ne'),
        href: whatsAppLink(
          pick(
            'Hello! I feel nervous about dental treatment and would like to ask a few questions before my visit.',
            'Добрий день! Я хвилююся перед лікуванням і хотів/хотіла б поставити кілька запитань перед візитом.',
            'Добрый день! Я волнуюсь перед лечением и хотел/хотела бы задать несколько вопросов перед визитом.',
            'Bună ziua! Am emoții înainte de tratament și aș dori să pun câteva întrebări înainte de vizită.',
          ),
        ),
        external: true,
      },
    },
    {
      q: pick(
        'Can I find out the cost first?',
        'Чи можна спочатку дізнатися вартість?',
        'Можно ли сначала узнать стоимость?',
        'Pot afla costul mai întâi?',
      ),
      a: pick(
        'Yes. Our main prices are listed on the website. Your dentist will explain the final treatment plan and cost after the exam, before any agreed treatment begins.',
        'Так. Основні ціни доступні на сайті. Остаточний план і вартість лікування лікар пояснить після огляду, до початку узгодженого лікування.',
        'Да. Основные цены доступны на сайте. Окончательный план и стоимость лечения врач объяснит после осмотра, до начала согласованного лечения.',
        'Da. Prețurile principale sunt disponibile pe site. Medicul vă va explica planul final și costul tratamentului după examinare, înainte de începerea tratamentului convenit.',
      ),
      link: pricesLink,
    },
    {
      q: pick(
        'Which languages can I use at Dentico?',
        'Чи можна спілкуватися українською?',
        'Можно ли общаться на русском?',
        'În ce limbi pot comunica la Dentico?',
      ),
      a: pick(
        'Our team speaks English, Ukrainian and Russian. When booking, let us know which language you prefer.',
        'Так, у Dentico можна спілкуватися українською. Під час запису повідомте, якою мовою вам зручніше спілкуватися.',
        'Да, в Dentico можно общаться на русском. При записи сообщите, на каком языке вам удобнее общаться.',
        'Echipa noastră vorbește engleză, ucraineană și rusă. La programare, spuneți-ne în ce limbă preferați să comunicați.',
      ),
    },
    {
      q: pick(
        'How do I book my first visit?',
        'Як записатися на перший візит?',
        'Как записаться на первый визит?',
        'Cum mă programez la prima vizită?',
      ),
      a: pick(
        "Message us on WhatsApp or choose a time through online booking. If you're not sure which service you need, briefly describe your request and our administrator will help you find the right option.",
        'Напишіть нам у WhatsApp або оберіть час через онлайн-запис. Якщо ви ще не знаєте, яка послуга потрібна, коротко опишіть свій запит — адміністратор допоможе зорієнтуватися.',
        'Напишите нам в WhatsApp или выберите время через онлайн-запись. Если вы ещё не знаете, какая услуга нужна, коротко опишите свой запрос — администратор поможет сориентироваться.',
        'Scrieți-ne pe WhatsApp sau alegeți o oră prin programarea online. Dacă nu știți încă de ce serviciu aveți nevoie, descrieți pe scurt solicitarea — administratorul vă va ajuta să vă orientați.',
      ),
    },
  ]
}
